import PropTypes from 'prop-types';
import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

import Logo from 'src/components/logo';
import Iconify from 'src/components/iconify';
import { useResponsive } from 'src/hooks/useResponsive';
import { adminMobileNavItems } from 'src/layouts/main/config-navigation';
import WalletConnectButton from 'src/components/wallet-connect/WalletConnectButton';

const AdminHeader = ({ toggleDrawer }) => {
  const theme = useTheme();
  const location = useLocation();

  const isDesktop = useResponsive('up', 'md');
  const showBrandText = useResponsive('up', 'lg');

  const isDark = theme.palette.mode === 'dark';

  return (
    <>
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          backgroundColor: alpha(theme.palette.background.default, isDark ? 0.82 : 0.78),
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          boxShadow: isDark ? '0 6px 18px rgba(0,0,0,0.22)' : '0 6px 18px rgba(15,23,42,0.06)',
        }}
      >
        <Box
          sx={{
            height: { xs: 64, md: 74 },
            px: { xs: 2, sm: 3, md: 4 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
              }}
            >
              <Logo />
            </Box>

            {showBrandText && (
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    fontWeight: 800,
                    letterSpacing: 0.2,
                    fontSize: { md: 18, lg: 20 },
                    lineHeight: 1.1,
                  }}
                >
                  SafiSwap
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    color: 'text.secondary',
                    fontWeight: 600,
                    letterSpacing: 0.4,
                  }}
                >
                  Admin Dashboard
                </Typography>
              </Box>
            )}
          </Stack>

          {isDesktop ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  p: 0.5,
                  borderRadius: 999,
                  backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.035),
                  border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                }}
              >
                {adminMobileNavItems.map((item) => {
                  const isActive =
                    location.pathname === item.path ||
                    location.pathname.startsWith(`${item.path}/`);

                  return (
                    <Button
                      key={item.title}
                      component={Link}
                      to={item.path}
                      disableElevation
                      sx={{
                        minHeight: 42,
                        px: 2,
                        borderRadius: 999,
                        fontWeight: 700,
                        fontSize: 14,
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                        color: isActive ? 'common.white' : 'text.primary',
                        background: isActive
                          ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                          : 'transparent',
                        boxShadow: isActive ? '0 8px 18px rgba(34,197,94,0.25)' : 'none',
                        '&:hover': {
                          background: isActive
                            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                            : alpha(theme.palette.text.primary, isDark ? 0.08 : 0.05),
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {item.title}
                    </Button>
                  );
                })}
              </Stack>

              <Box sx={{ ml: 0.5 }}>
                <WalletConnectButton />
              </Box>
            </Stack>
          ) : (
            <IconButton
              onClick={toggleDrawer}
              size="large"
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2.5,
                border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.06 : 0.04),
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.primary, isDark ? 0.1 : 0.07),
                },
              }}
            >
              <Iconify icon="material-symbols:menu-rounded" width={26} />
            </IconButton>
          )}
        </Box>
      </Box>

      <Divider sx={{ display: { xs: 'block', md: 'none' }, opacity: 0 }} />
    </>
  );
};

AdminHeader.propTypes = {
  toggleDrawer: PropTypes.func,
};

export default AdminHeader;
